<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core';
import debounce from 'debounce';

// icons
import { faTimesCircle } from '@fortawesome/pro-light-svg-icons';
import { faUndoAlt, faChevronRight } from '@fortawesome/pro-regular-svg-icons';

// components
import CiFanclubFilter from '@/components/fanclub/CiFanclubFilter.vue';
import CiFilterGroup from '@/components/filter/CiFilterPanel/CiFilterGroup.vue';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiFilterModalProps {
  excludedFilter?: any;
  groupsToDisplay?: any;
  showSidebar?: boolean;
}
const props = withDefaults(defineProps<CiFilterModalProps>(), {
  excludedFilter: () => [],
  groupsToDisplay: false,
  showSidebar: true,
});

const emit = defineEmits<{
  close: [];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const getRouteBaseName = useRouteBaseName();
const route = useRoute();
const searchStore = useSearchStore();
const { $gettext } = useGettext();
const {
  activeFilterCount,
  filterGroups,
  getActiveFilterByGroup,
  getFilterByGroupName,
  openResetModal,
  selectedFilterGroupLabel,
  setFilter,
} = useFilter();

/********************
 * REFS & VARS       *
 ********************/
const filterModalPaneRef = ref<HTMLElement | null>(null);
const filterModalRef = ref<HTMLElement | null>(null);
const showFilterPane = ref(false);
const windowInnerWidth = ref(window.innerWidth);

const numberOfResults = computed(() => {
  return getRouteBaseName(route) === RBN_SEARCH_MAP ? searchStore.mapResultCount : searchStore.resultCount;
});

const filteredGroups = computed(() => {
  return props.groupsToDisplay ? props.groupsToDisplay : filterGroups;
});

/********************
 * HOOKS             *
 ********************/
useResizeObserver(filterModalRef, debounce(() => {
  windowInnerWidth.value = window.innerWidth;
}));

/********************
 * FUNCTIONS         *
 ********************/
function onChangeGroup(name: string | null) {
  showFilterPane.value = true;

  if (searchStore.selectedFilterGroup === name) {
    return;
  }

  searchStore.selectedFilterGroup = name;

  nextTick(() => {
    if (filterModalPaneRef.value) {
      filterModalPaneRef.value.scroll({
        top: 0,
      });
    }
  });
}
</script>

<template>
  <div
    ref="filterModalRef"
    class="filter-modal container-fluid flex h-full flex-col overflow-hidden"
  >
    <div class="row border-b border-gray-40 bg-gray-10 py-2">
      <div class="col-12 flex">
        <CiButton
          v-if="showFilterPane && windowInnerWidth < 576"
          class="button"
          icon-before
          @click="showFilterPane = false"
        >
          <span>{{ $gettext('Back to Overview') }}</span>
        </CiButton>
        <div class="shrink grow basis-auto self-center text-center">
          <strong v-if="!showFilterPane || windowInnerWidth > 575">{{ $gettext('Filter') }}</strong>
        </div>
        <CiButton
          class="button button--circle"
          :icon="faTimesCircle"
          icon-ratio="1.2"
          @click="emit('close')"
        />
      </div>
      <div class="col-sm flex justify-end" />
    </div>

    <div class="row grow overflow-hidden">
      <!-- sidebar -->
      <template v-if="props.showSidebar">
        <div
          v-show="!showFilterPane || windowInnerWidth > 575"
          class="filter-modal__groups col-12 col-sm-4 h-full grow overflow-y-auto overflow-x-hidden bg-white !px-0 sm:bg-gray-20"
        >
          <div
            v-if="activeFilterCount"
            class="filter-modal__groups--item cursor-pointer overflow-y-auto overflow-x-hidden border-0 py-4 sm:px-2"
          >
            <div class="row">
              <div class="col-12 flex justify-center">
                <CiButton
                  class="button button--warning-outline"
                  :icon="faUndoAlt"
                  icon-ratio="0.5"
                  @click="openResetModal()"
                >
                  <span>{{ $gettext('Reset All Filter') }}</span>
                </CiButton>
              </div>
            </div>
          </div>
          <div
            v-for="(group, index) in filteredGroups"
            :key="`group-mobile-${index}`"
            :class="{
              active: searchStore.selectedFilterGroup
                ? searchStore.selectedFilterGroup === group.name
                : group.name === 'fanclub',
            }"
            class="filter-modal__groups--item flex cursor-pointer items-center py-2"
            @click="onChangeGroup(group?.name || null)"
          >
            <div
              :class="{
                'has-sublabel': getActiveFilterByGroup(group.name).length > 0,
              }"
              class="filter-modal__groups--wrapper shrink grow basis-auto truncate"
            >
              <div class="label truncate leading-4">
                {{ group.label }}
              </div>
              <div
                v-if="getActiveFilterByGroup(group.name).length > 0"
                class="sublabel mt-1 truncate text-gray"
                v-text="getActiveFilterByGroup(group.name).join(', ')"
              />
            </div>
            <CiAwesomeIcon
              :icon="faChevronRight"
              class="mr-2 fill-primary"
              ratio="0.75"
            />
          </div>
        </div>
      </template>

      <!-- filter -->
      <div
        v-show="showFilterPane || windowInnerWidth > 575 || !props.showSidebar"
        ref="filterModalPaneRef"
        class="filter-modal__pane h-full overflow-y-auto !px-4 !pb-4 sm:!px-12"
        :class="[props.showSidebar ? 'col-12 col-sm-8' : 'col-12']"
      >
        <!-- only selected filter -->
        <template v-if="props.showSidebar">
          <h3 class="my-6 text-center">
            {{ selectedFilterGroupLabel }}
          </h3>
          <CiFanclubFilter
            v-if="!searchStore.selectedFilterGroup || searchStore.selectedFilterGroup === 'fanclub'"
            :small="true"
            @change="setFilter"
          />
          <CiFilterGroup
            v-else
            :filter="getFilterByGroupName(searchStore.selectedFilterGroup || 'rating', props.excludedFilter)"
            :selected-filter="searchStore.selectedFilter"
            @change="setFilter"
          />
          <div v-if="getActiveFilterByGroup(searchStore.selectedFilterGroup || 'rating').length > 0">
            <hr class="my-4 h-px border-0 bg-gray-40" />
            <div class="flex justify-center">
              <CiButton
                class="button button--warning-outline mb-6"
                :icon="faUndoAlt"
                icon-ratio="0.5"
                @click="openResetModal(searchStore.selectedFilterGroup, props.excludedFilter)"
              >
                <span>{{ $gettext('Reset Filter') }}</span>
              </CiButton>
            </div>
          </div>
        </template>

        <!-- all filter from groupsToDisplay -->
        <template v-else>
          <div
            v-for="group in props.groupsToDisplay"
            :key="group.name"
            class="mb-12"
          >
            <h3 class="my-6 text-center">
              {{ group.label }}
            </h3>
            <CiFilterGroup
              :filter="getFilterByGroupName(group.name, props.excludedFilter)"
              :selected-filter="searchStore.selectedFilter"
              @change="setFilter"
            />
            <div
              v-if="getActiveFilterByGroup(group.name, props.excludedFilter).length > 0"
              :key="group.name"
            >
              <hr class="my-4 h-px border-0 bg-gray-40" />
              <div class="flex justify-center">
                <CiButton
                  class="button button--warning-outline mb-4"
                  :icon="faUndoAlt"
                  icon-ratio="0.5"
                  @click="openResetModal(group.name, excludedFilter)"
                >
                  <span>{{ $gettext('Reset Filter') }}</span>
                </CiButton>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div
      class="row z-10 min-h-14 border-t border-gray-40 bg-white py-2 shadow-lg"
      :class="[showFilterPane || windowInnerWidth > 575 ? 'min-h-[7.0625rem] sm:min-h-14' : 'min-h-14']"
    >
      <div
        v-if="showFilterPane || windowInnerWidth > 575"
        class="col-12 col-md-6 col-lg-3 offset-lg-3 md:order-0 order-1 mt-4 flex items-center sm:hidden md:mt-0"
      >
        <CiButton
          :disabled="getActiveFilterByGroup(searchStore.selectedFilterGroup).length === 0"
          class="button button--warning-outline icon-md-absolute w-full"
          @click="openResetModal(searchStore.selectedFilterGroup, excludedFilter)"
        >
          <span>{{ $gettext('Reset Filter') }}</span>
        </CiButton>
      </div>
      <div class="col-12 col-sm-6 offset-sm-3">
        <CiButton
          class="button button--primary icon-md-absolute w-full"
          @click="emit('close')"
        >
          <span>
            {{ $gettext('Hits %{count}', { count: numberOfResults }) }}
          </span>
        </CiButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.filter-modal__groups--item {
  border-bottom: 1px solid $light-gray-40;
  margin: 0 1rem;

  @include media-breakpoint-up(sm) {
    margin: 0;
  }

  .filter-modal__groups--wrapper {
    padding: 1rem;

    &.has-sublabel {
      padding-bottom: 0.5rem;
      padding-top: 0.5rem;

      .sublabel {
        height: 1rem;
      }
    }
  }

  .sublabel {
    font-size: 0.75rem;
    height: 0rem;
    line-height: 1rem;
  }

  &.active {
    background-color: $white;
  }
}
</style>
